/**
 * Typed query/mutation hooks for `/api/bi/crm/settings/*` endpoints.
 *
 * Mirrors the JSON shape produced by the Django views in
 * `src/crm/views/settings_*`. Decimal-style fields are strings, durations
 * are seconds (integers, nullable), and the password on EmailSettings is
 * intentionally write-only — the GET response only carries `has_password`.
 */
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient, get, post } from '../client';
import { svcGet, svcPatch } from '../svc/client';
import type {
  CrmAvoidableHint,
  CrmReason,
  CrmReasonCategory,
  CrmTicketPriority,
  CrmTicketType,
} from './crm';

export type CrmRoutingSource = 'auto' | 'manual' | 'api';

export type CrmRoutingTargetKind = 'user' | 'role';

export interface CrmRoutingTarget {
  id?: number;
  kind: CrmRoutingTargetKind;
  user_id: number | null;
  user_name?: string | null;
  user_email?: string | null;
  role: string;
  is_primary: boolean;
  is_watcher: boolean;
  notify_in_app: boolean;
  notify_email: boolean;
  notify_whatsapp: boolean;
}

export interface CrmRoutingTier {
  id?: number;
  position: number;
  label: string;
  wait_seconds_after_previous: number;
  notify_event_kind: string;
  targets: CrmRoutingTarget[];
}

export interface CrmRoutingRule {
  id: number;
  name: string;
  description: string;
  is_enabled: boolean;
  evaluation_order: number;
  match_types: CrmTicketType[];
  match_priorities: CrmTicketPriority[];
  match_sources: CrmRoutingSource[];
  match_all_stores: boolean;
  match_store_ids: number[];
  response_sla_seconds: number | null;
  resolution_sla_seconds: number | null;
  ack_sla_seconds: number | null;
  pause_on_waiting_customer: boolean;
  tiers: CrmRoutingTier[];
  created_at: string | null;
  updated_at: string | null;
}

export interface CrmRoutingRulePayload {
  name: string;
  description?: string;
  is_enabled?: boolean;
  evaluation_order?: number;
  match_types?: CrmTicketType[];
  match_priorities?: CrmTicketPriority[];
  match_sources?: CrmRoutingSource[];
  match_all_stores?: boolean;
  match_store_ids?: number[];
  response_sla_seconds?: number | null;
  resolution_sla_seconds?: number | null;
  ack_sla_seconds?: number | null;
  pause_on_waiting_customer?: boolean;
  tiers?: CrmRoutingTier[];
}

export interface CrmRoutingPreviewBody {
  type: CrmTicketType;
  priority: CrmTicketPriority;
  store_id?: number | null;
  source?: CrmRoutingSource;
}

export interface CrmRoutingPreviewResult {
  rule_id: number | null;
  rule_name: string | null;
  tier_position: number | null;
  primary_user_id: number | null;
  assignee_user_ids: number[];
  watcher_user_ids: number[];
  /** Recipients the engine would WS-push when this tier fires. */
  in_app_user_ids: number[];
  /** Recipients the engine would email when this tier fires. */
  email_user_ids: number[];
  /** Recipients the engine would WhatsApp-message when this tier fires. */
  whatsapp_user_ids: number[];
  response_sla_seconds: number | null;
  resolution_sla_seconds: number | null;
  ack_sla_seconds: number | null;
}

// ---------------------------------------------------------------------------
// SMTP settings
// ---------------------------------------------------------------------------

export interface CrmEmailSettings {
  host: string;
  port: number;
  use_tls: boolean;
  use_ssl: boolean;
  username: string;
  from_email: string;
  from_name: string;
  is_enabled: boolean;
  has_password: boolean;
  last_test_at: string | null;
  last_test_status: string;
  last_test_error: string;
  updated_at: string | null;
}

export interface CrmEmailSettingsPayload {
  host?: string;
  port?: number;
  use_tls?: boolean;
  use_ssl?: boolean;
  username?: string;
  from_email?: string;
  from_name?: string;
  is_enabled?: boolean;
  /** Plain-text password — encrypted at rest server-side. Empty string clears it. */
  password?: string;
}

export interface CrmChannelAccount {
  channel: string;
  is_enabled: boolean;
  display_name: string;
  meta_app_id: string;
  meta_page_id: string;
  meta_ig_user_id: string;
  cloud_phone_number_id: string;
  cloud_waba_id: string;
  wa_template_fallback: string;
  wa_template_language: string;
  meta_verify_token: string;
  has_meta_app_secret: boolean;
  has_meta_access_token: boolean;
  last_sync_at: string | null;
  last_error: string;
  updated_at: string | null;
}

/** Gmail customer-channel row on the BI service (SMTP send + IMAP inbound). */
export interface CrmGmailChannel {
  channel: string;
  is_enabled: boolean;
  display_name: string;
  username: string;
  has_password: boolean;
  from_name: string;
  from_email: string;
  smtp_host: string;
  smtp_port: number;
  smtp_use_tls: boolean;
  smtp_use_ssl: boolean;
  imap_host: string;
  imap_port: number;
  imap_use_ssl: boolean;
  inbound_enabled: boolean;
  last_poll_at: string | null;
  last_poll_status: string;
  last_poll_error: string;
  last_error: string;
  updated_at: string | null;
}

export interface CrmMetaSettings {
  whatsapp_customer: CrmChannelAccount;
  instagram: CrmChannelAccount;
  facebook_messenger: CrmChannelAccount;
  gmail?: CrmGmailChannel;
}

export interface CrmAiSettings {
  is_enabled: boolean;
  model: string;
  base_url: string;
  has_api_key: boolean;
  confidence_threshold: number;
  auto_intents: string[];
  escalate_intents: string[];
  max_auto_replies_per_hour: number;
  system_prompt: string;
  faq_corpus: string;
  // v2: identity & verification
  require_order_and_mobile: boolean;
  auto_tag_on_verify: boolean;
  // v2: language & tone
  reply_language: string;
  chat_style_notes: string;
  email_style_notes: string;
  email_greeting_style: string;
  email_signature: string;
  // v2: capabilities
  vision_enabled: boolean;
  transcription_enabled: boolean;
  transcription_model: string;
  smart_compose_enabled: boolean;
  location_link_ttl_minutes: number;
  last_error: string;
  updated_at: string | null;
}

// ---------------------------------------------------------------------------
// Engagement + ops key-value settings (fasto_bi service)
// ---------------------------------------------------------------------------

export interface CrmReengagementSettings {
  enabled: boolean;
  template_name: string;
  template_language: string;
  delay_hours: number;
  max_window_days: number;
  quiet_hours_start: string;
  quiet_hours_end: string;
  max_per_contact_per_month: number;
}

export interface CrmMediaSettings {
  retention_days: number;
  max_download_mb: number;
}

export interface CrmNotificationPrefs {
  desktop_enabled: boolean;
  sound_enabled: boolean;
}

export interface CrmEngagementSettings {
  reengagement: CrmReengagementSettings;
  media: CrmMediaSettings;
  notifications: CrmNotificationPrefs;
}

export interface CrmEmailTestBody {
  to_email: string;
  /**
   * Optional override; when omitted server falls back to the persisted
   * password. Test-send uses the same resolver as production sends so a
   * passing test guarantees real notifications will go through.
   */
  password?: string;
  host?: string;
  port?: number;
  use_tls?: boolean;
  use_ssl?: boolean;
  username?: string;
  from_email?: string;
  from_name?: string;
  /**
   * Mirror of the `is_enabled` flag from the form. The server now honours
   * this gate even on test-send (no force-true), so omitting it falls
   * through to whatever the saved row has. Send `true` to validate
   * BEFORE flipping the saved flag on.
   */
  is_enabled?: boolean;
}

export interface CrmEmailTestResponse {
  ok: boolean;
  error: string | null;
  last_test_at: string;
}

// ---------------------------------------------------------------------------
// WhatsApp settings
// ---------------------------------------------------------------------------

export type CrmWhatsAppProvider = 'cloud_api' | 'qr_web';

export interface CrmWhatsAppSettings {
  is_enabled: boolean;
  provider: CrmWhatsAppProvider;
  cloud_phone_number_id: string;
  cloud_waba_id: string;
  has_cloud_access_token: boolean;
  template_name_assigned: string;
  template_name_escalated: string;
  template_language: string;
  qr_service_base_url: string;
  has_qr_service_token: boolean;
  last_connected_at: string | null;
  last_test_at: string | null;
  last_test_status: string;
  last_error: string;
  updated_at: string | null;
}

export interface CrmWhatsAppSettingsPayload {
  is_enabled?: boolean;
  provider?: CrmWhatsAppProvider;
  cloud_phone_number_id?: string;
  cloud_waba_id?: string;
  cloud_access_token?: string;
  template_name_assigned?: string;
  template_name_escalated?: string;
  template_language?: string;
  qr_service_base_url?: string;
  qr_service_token?: string;
}

export interface CrmWhatsAppTestBody extends CrmWhatsAppSettingsPayload {
  to_phone: string;
}

export interface CrmWhatsAppTestResponse {
  ok: boolean;
  error: string | null;
  last_test_at: string;
}

export interface CrmWhatsAppQrStatus {
  connected?: boolean;
  status?: string;
  qr?: string | null;
  error?: string | null;
  provider?: string;
}

// ---------------------------------------------------------------------------
// Notification templates
// ---------------------------------------------------------------------------

export interface CrmNotificationTemplate {
  id: number;
  event_kind: string;
  event_label: string;
  subject: string;
  body_markdown: string;
  is_active: boolean;
  updated_at: string | null;
}

export interface CrmNotificationTemplatesResponse {
  results: CrmNotificationTemplate[];
  event_kinds: Array<{ value: string; label: string }>;
}

export interface CrmTemplateUpdate {
  event_kind: string;
  subject?: string;
  body_markdown?: string;
  is_active?: boolean;
}

export interface CrmTemplateVariable {
  key: string;
  label: string;
}

// ---------------------------------------------------------------------------
// Cancellation source toggles
// ---------------------------------------------------------------------------

export interface CrmCancellationSource {
  source: string;
  label: string;
  is_auto_create: boolean;
  default_priority: CrmTicketPriority;
  updated_at: string | null;
}

export interface CrmCancellationUpdate {
  source: string;
  is_auto_create?: boolean;
  default_priority?: CrmTicketPriority;
  label?: string;
}

// ---------------------------------------------------------------------------
// SLA & breach policy + operational stage limits
// ---------------------------------------------------------------------------

/**
 * Mirrors `crm.models.CrmSlaPolicy`. Three independent knobs:
 *
 *   - `order_tag_grace_seconds` — minimum lateness before an OrderTag
 *     breach is recorded (and therefore before any auto-ticket fires).
 *   - `priority_*_after_seconds` — replace the historical hard-coded
 *     5-minute / 20-minute floors in `crm.signals._ensure_sla_ticket`.
 *   - `require_*_for_resolution` — optional resolve-time guards that
 *     block close on `sla_breach` tickets without a populated reason or
 *     evidence (call recording or minimum-length resolution note).
 */
export interface CrmSlaPolicy {
  order_tag_grace_seconds: number;
  priority_normal_after_seconds: number;
  priority_high_after_seconds: number;
  require_reason_for_resolution: boolean;
  require_evidence_for_resolution: boolean;
  min_resolution_note_length: number;
  effective_from: string | null;
  updated_at: string | null;
}

export interface CrmSlaPolicyPayload {
  order_tag_grace_seconds?: number;
  priority_normal_after_seconds?: number;
  priority_high_after_seconds?: number;
  require_reason_for_resolution?: boolean;
  require_evidence_for_resolution?: boolean;
  min_resolution_note_length?: number;
  /** ISO datetime; omit to stamp `now()` server-side. */
  effective_from?: string | null;
}

/**
 * Mirrors `singleton_model.SupportConfiguration` — the operational source
 * of truth for "how late is too late" used by `order/utils.is_*_late`.
 * `null` disables the corresponding stage SLA.
 */
export interface CrmSlaStageLimits {
  sku_count_limit: number | null;
  packing_normal_time: number | null;
  packing_limit_time: number | null;
  biller_normal_time: number | null;
  biller_limit_time: number | null;
  picking_time: number | null;
  support_action_time: number | null;
  rider_time: number | null;
  secondary_rider_time: number | null;
  tertiary_rider_time: number | null;
  /** Reached → completed handover (seconds). */
  delivery_handover_seconds: number | null;
  /** Rider returned to store → biller processed return (seconds). */
  return_entry_seconds: number | null;
  /** Biller processed return → packer racked/restored return (seconds). */
  return_racking_seconds: number | null;
}

export type CrmSlaStageLimitsPayload = Partial<CrmSlaStageLimits>;

export interface CrmSlaPolicyResponse {
  policy: CrmSlaPolicy;
  stage_limits: CrmSlaStageLimits;
}

// ---------------------------------------------------------------------------
// Hooks: routing rules
// ---------------------------------------------------------------------------

function invalidateSettings(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'settings'] });
}

export function useRoutingRules() {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'routing-rules'],
    queryFn: () =>
      get<{ results: CrmRoutingRule[] }>('/api/bi/crm/settings/routing-rules/'),
    staleTime: 30_000,
  });
}

export function useRoutingRule(id: number | null | undefined) {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'routing-rule', id ?? null],
    queryFn: () => get<CrmRoutingRule>(`/api/bi/crm/settings/routing-rules/${id}/`),
    enabled: id != null && id > 0,
  });
}

export function useCreateRoutingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CrmRoutingRulePayload) =>
      post<CrmRoutingRule>('/api/bi/crm/settings/routing-rules/', body),
    onSuccess: () => invalidateSettings(qc),
  });
}

export function usePatchRoutingRule(id: number | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CrmRoutingRulePayload) => {
      if (id == null) throw new Error('Missing rule id');
      const r = await apiClient.patch<CrmRoutingRule>(
        `/api/bi/crm/settings/routing-rules/${id}/`,
        body,
      );
      return r.data;
    },
    onSuccess: () => invalidateSettings(qc),
  });
}

export function useDeleteRoutingRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/bi/crm/settings/routing-rules/${id}/`);
      return id;
    },
    onSuccess: () => invalidateSettings(qc),
  });
}

export function useRoutingPreview() {
  return useMutation({
    mutationFn: (body: CrmRoutingPreviewBody) =>
      post<CrmRoutingPreviewResult>('/api/bi/crm/settings/routing-rules/preview/', body),
  });
}

// ---------------------------------------------------------------------------
// Hooks: email
// ---------------------------------------------------------------------------

export function useEmailSettings() {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'email'],
    queryFn: () => get<CrmEmailSettings>('/api/bi/crm/settings/email/'),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function usePatchEmailSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CrmEmailSettingsPayload) => {
      const r = await apiClient.patch<CrmEmailSettings>('/api/bi/crm/settings/email/', body);
      return r.data;
    },
    onSuccess: () => invalidateSettings(qc),
  });
}

export function useTestEmailSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CrmEmailTestBody) =>
      post<CrmEmailTestResponse>('/api/bi/crm/settings/email/test/', body),
    onSuccess: () => invalidateSettings(qc),
  });
}

export function useWhatsAppSettings() {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'whatsapp'],
    queryFn: () => get<CrmWhatsAppSettings>('/api/bi/crm/settings/whatsapp/'),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// Meta channels + AI policy live on the fasto_bi service (/svc/crm/*), which
// stores them in its own database — not on Django.

export function useMetaSettings() {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'meta'],
    queryFn: () => svcGet<CrmMetaSettings>('/svc/crm/settings/meta/'),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function usePatchMetaSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CrmMetaSettings>) =>
      svcPatch<CrmMetaSettings>('/svc/crm/settings/meta/', body),
    onSuccess: () => invalidateSettings(qc),
  });
}

export function useAiSettings() {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'ai'],
    queryFn: () => svcGet<CrmAiSettings>('/svc/crm/settings/ai/'),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function usePatchAiSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      svcPatch<CrmAiSettings>('/svc/crm/settings/ai/', body),
    onSuccess: () => invalidateSettings(qc),
  });
}

export function useEngagementSettings() {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'engagement'],
    queryFn: () => svcGet<CrmEngagementSettings>('/svc/crm/settings/engagement/'),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function usePatchEngagementSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<CrmEngagementSettings>) =>
      svcPatch<CrmEngagementSettings>('/svc/crm/settings/engagement/', body),
    onSuccess: () => invalidateSettings(qc),
  });
}

export function usePatchWhatsAppSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CrmWhatsAppSettingsPayload) => {
      const r = await apiClient.patch<CrmWhatsAppSettings>(
        '/api/bi/crm/settings/whatsapp/',
        body,
      );
      return r.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(['bi', 'crm', 'settings', 'whatsapp'], data);
      void invalidateSettings(qc);
      void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'settings', 'whatsapp', 'status'] });
      void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'settings', 'whatsapp', 'qr'] });
    },
  });
}

export function useTestWhatsAppSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CrmWhatsAppTestBody) =>
      post<CrmWhatsAppTestResponse>('/api/bi/crm/settings/whatsapp/test/', body),
    onSuccess: () => invalidateSettings(qc),
  });
}

/**
 * @param enabled When false, the query is inactive.
 * @param pollActive When true with enabled, refetches on an interval (only after server has QR config).
 */
export function useWhatsAppStatus(enabled = true, pollActive = false) {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'whatsapp', 'status'],
    queryFn: () => get<CrmWhatsAppQrStatus>('/api/bi/crm/settings/whatsapp/status/'),
    staleTime: 10_000,
    refetchInterval: enabled && pollActive ? 15_000 : false,
    enabled,
  });
}

export function useWhatsAppQr(enabled = true) {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'whatsapp', 'qr'],
    queryFn: () => get<CrmWhatsAppQrStatus>('/api/bi/crm/settings/whatsapp/qr/'),
    staleTime: 5_000,
    enabled,
  });
}

export function useLogoutWhatsAppSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<{ ok?: boolean; error?: string | null }>(
      '/api/bi/crm/settings/whatsapp/logout/',
      {},
    ),
    onSuccess: () => invalidateSettings(qc),
  });
}

// ---------------------------------------------------------------------------
// Hooks: templates
// ---------------------------------------------------------------------------

export function useNotificationTemplates() {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'notification-templates'],
    queryFn: () =>
      get<CrmNotificationTemplatesResponse>('/api/bi/crm/settings/notification-templates/'),
    staleTime: 60_000,
  });
}

export function useTemplateVariables() {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'notification-template-variables'],
    queryFn: () =>
      get<{ results: CrmTemplateVariable[] }>(
        '/api/bi/crm/settings/notification-templates/variables/',
      ),
    staleTime: 5 * 60_000,
  });
}

export function usePatchNotificationTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updates: CrmTemplateUpdate[]) => {
      const r = await apiClient.patch<{ results: CrmNotificationTemplate[] }>(
        '/api/bi/crm/settings/notification-templates/',
        { updates },
      );
      return r.data;
    },
    onSuccess: () => invalidateSettings(qc),
  });
}

// ---------------------------------------------------------------------------
// Hooks: cancellation source toggles
// ---------------------------------------------------------------------------

export function useCancellationSources() {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'cancellation-sources'],
    queryFn: () =>
      get<{
        results: CrmCancellationSource[];
        priorities: Array<{ value: CrmTicketPriority; label: string }>;
      }>('/api/bi/crm/settings/cancellation-sources/'),
    staleTime: 60_000,
  });
}

export function usePatchCancellationSources() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updates: CrmCancellationUpdate[]) => {
      const r = await apiClient.patch<{ results: CrmCancellationSource[] }>(
        '/api/bi/crm/settings/cancellation-sources/',
        { updates },
      );
      return r.data;
    },
    onSuccess: () => invalidateSettings(qc),
  });
}

// ---------------------------------------------------------------------------
// Hooks: SLA & breach policy + stage limits
// ---------------------------------------------------------------------------

export function useSlaPolicy() {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'sla-policy'],
    queryFn: () => get<CrmSlaPolicyResponse>('/api/bi/crm/settings/sla-policy/'),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function usePatchSlaPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CrmSlaPolicyPayload) => {
      const r = await apiClient.patch<CrmSlaPolicyResponse>(
        '/api/bi/crm/settings/sla-policy/',
        body,
      );
      return r.data;
    },
    onSuccess: () => invalidateSettings(qc),
  });
}

export function usePatchSlaStageLimits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CrmSlaStageLimitsPayload) => {
      const r = await apiClient.patch<CrmSlaPolicyResponse>(
        '/api/bi/crm/settings/sla-policy/stage-limits/',
        body,
      );
      return r.data;
    },
    onSuccess: () => invalidateSettings(qc),
  });
}

// ---------------------------------------------------------------------------
// Reason codes (admin taxonomy — all categories)
// ---------------------------------------------------------------------------

export interface CrmReasonCodesSettingsResponse {
  results: CrmReason[];
}

function invalidateReasonCodeQueries(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'reason-codes'] });
  void qc.invalidateQueries({ queryKey: ['bi', 'crm', 'settings', 'reason-codes'] });
}

export function useReasonCodesSettings(category: CrmReasonCategory) {
  return useQuery({
    queryKey: ['bi', 'crm', 'settings', 'reason-codes', category],
    queryFn: () =>
      get<CrmReasonCodesSettingsResponse>('/api/bi/crm/settings/reason-codes/', {
        params: { category },
      }),
  });
}

export interface CreateReasonCodeBody {
  category: CrmReasonCategory;
  code: string;
  label: string;
  parent_id?: number | null;
  order?: number;
  is_avoidable_hint?: CrmAvoidableHint;
  is_active?: boolean;
}

export function useCreateReasonCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateReasonCodeBody) =>
      post<CrmReason>('/api/bi/crm/settings/reason-codes/', body),
    onSuccess: () => invalidateReasonCodeQueries(qc),
  });
}

export interface PatchReasonCodeBody {
  label?: string;
  order?: number;
  parent_id?: number | null;
  is_active?: boolean;
  is_avoidable_hint?: CrmAvoidableHint;
}

export function usePatchReasonCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: PatchReasonCodeBody }) => {
      const r = await apiClient.patch<CrmReason>(`/api/bi/crm/settings/reason-codes/${id}/`, body);
      return r.data;
    },
    onSuccess: () => invalidateReasonCodeQueries(qc),
  });
}
